// eslint-disable-next-line @typescript-eslint/no-unused-vars -- parked with company-level permissions
import { CompanyToolsCard } from '../../pages/orbit-room/CompanyToolsCard';
// Company (shared) MCP servers are off for now: every person connects their own.
import React, { useEffect, useState } from 'react';
import { ArrowLeft, KeyRound, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { getMyProfile, updateMyPassword, updateMyProfile } from '../../services/api';
import { useAsync } from '../../pages/aai/useAsync';
import { usePermissions } from '../../hooks/usePermissions';
import type {
  AaiContext, AaiPolicy, AaiSkill, ActivityEvent, ActivityFeed, AiControl, GrantsView,
  MyPreferencesResponse,
} from '../../types/aai';
import {
  Action, Banner, Chip, Divided, EmptyState, ErrorNote, FactList, Field, FieldGrid, Input,
  ListRow, Loading, NotMeasured, Panel, PanelHead, RouteMark, SaveBar, Stat, Toggle,
} from '../aa';
import { CapabilityDrawer } from '../../pages/owner/CapabilityDrawer';
import { takePendingPermissionsTool } from '../../pages/owner/ControlDrawers';
import { ownerLinks } from '../../pages/owner/CompanyContextPage';
import { EventDrawer } from '../../pages/partner/ActivityView';
import PlanAndUsage from '../../pages/partner/PlanAndUsage';
import { ConnectorsPanel } from './connectors/ConnectorsPanel';
import CompanyTabs, { type CompanyTabId } from '../company/CompanyTabs';
import { agentsApi } from '../../services/agentsApi';
import { audienceOf } from '../../pages/owner/WorkRows';
import { relativeTime } from '../../pages/partner/format';

/*
 * The Owner's settings panels, on the portal kit.
 *
 * Each panel reads real state and offers only the actions the Owner holds.
 * Where the platform does not record something yet (session policy, data
 * region, a numeric release floor) the panel says so rather than drawing a
 * control that would save nothing. Configuration that belongs to Agents
 * Anywhere or the AI Champion is shown read-only with the reason.
 */

const initialsOf = (input: string): string => String(input || 'U')
  .split(/[\s@.]+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U';

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner', AI_CHAMPION: 'AI Champion', DEPARTMENT_HEAD: 'Department Head', AI_PARTICIPANT: 'Employee',
  TRAINER: 'Trainer', EXPERT: 'Expert', CONSULTANT: 'Solution Partner',
};

export const roleLabel = (role: string | null | undefined): string => {
  if (!role) return 'Member';
  return ROLE_LABEL[role] || role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const closeSettings = () => window.dispatchEvent(new Event('settings:close'));

/* ── Account ───────────────────────────────────────────────────────────── */

interface Profile {
  name?: string; email?: string; phone?: string; department?: string | null; profile_image?: string | null;
  sign_in_method?: 'google' | 'password' | null;
}

const GoogleMark: React.FC = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

interface SignInRow { mark: React.ReactNode; label: string; passwordDesc: string; passwordAction: string }

const signInRow = (method: Profile['sign_in_method']): SignInRow => {
  if (method === 'google') {
    return {
      mark: <GoogleMark />,
      label: 'Signed in with Google',
      passwordDesc: 'You sign in with Google. Set a password to also sign in with your email.',
      passwordAction: 'Set a password',
    };
  }
  let label = 'Email';
  if (method === 'password') label = 'Email and password';
  return {
    mark: <Mail size={16} />,
    label,
    passwordDesc: 'Change it any time. Your sessions stay signed in.',
    passwordAction: 'Change password',
  };
};

export const AccountPanel: React.FC<{ clientName: string }> = ({ clientName }) => {
  const context = useAsync<AaiContext>(() => aaiApi.getContext(), 'Could not load your account.');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [saved, setSaved] = useState<Profile | null>(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState({ new_password: '', confirm_password: '' });
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    getMyProfile().then((p: Profile) => { setProfile(p); setSaved(p); }).catch(() => toast.error('Could not load your profile.'));
  }, []);

  if (!profile || !saved) return <Loading label="Loading your account" />;

  const dirty = profile.name !== saved.name || profile.phone !== saved.phone;
  const role = roleLabel(context.data?.workspace_role);
  const team = context.data?.department || profile.department || 'Not placed in a department';
  const signIn = signInRow(profile.sign_in_method);
  let passwordAction = signIn.passwordAction;
  if (changing) passwordAction = 'Cancel';

  const save = async () => {
    setBusy(true);
    try {
      await updateMyProfile({ name: profile.name, phone: profile.phone });
      localStorage.setItem('user_name', profile.name || '');
      window.dispatchEvent(new Event('user-profile-updated'));
      setSaved(profile);
      toast.success('Account saved.');
    } catch {
      toast.error('Could not save your account.');
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (password.new_password.length < 8) {
      toast.error('Use at least 8 characters.');
      return;
    }
    if (password.new_password !== password.confirm_password) {
      toast.error('The two passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await updateMyPassword(password);
      setPassword({ new_password: '', confirm_password: '' });
      setChanging(false);
      toast.success('Password changed.');
    } catch {
      toast.error('Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1000px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-tile bg-ink text-[13px] font-bold text-white">{initialsOf(profile.name || profile.email || '')}</span>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-bold leading-tight text-ink">{profile.name || profile.email}</div>
            <div className="truncate text-[12.5px] text-ink-dim">{role} · {team} · {clientName}</div>
          </div>
        </div>
        <Chip tone="ok">Active</Chip>
      </div>

      <FieldGrid className="mt-6">
        <Field label="Display name" htmlFor="account-name">
          <Input id="account-name" value={profile.name || ''} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
        </Field>
        <Field label="Phone" htmlFor="account-phone">
          <Input id="account-phone" value={profile.phone || ''} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Optional" />
        </Field>
        <Field label="Team" htmlFor="account-team" managedBy="Set by the organisation">
          <Input id="account-team" value={team} disabled />
        </Field>
        <Field label="Role" htmlFor="account-role" managedBy="Set by the Owner">
          <Input id="account-role" value={role} disabled />
        </Field>
      </FieldGrid>

      <Panel className="mt-6">
        <PanelHead kicker="Sign-in" title="How you get in" />
        <Divided>
          <ListRow
            mark={<RouteMark icon={signIn.mark} size="md" />}
            title="Sign-in method"
            desc={`${signIn.label} · ${profile.email}`}
          />
          <ListRow
            mark={<RouteMark icon={<KeyRound size={16} />} size="md" />}
            title="Password"
            desc={signIn.passwordDesc}
            right={<Action size="sm" variant="quiet" onClick={() => setChanging((v) => !v)}>{passwordAction}</Action>}
          />
          {changing && (
            <div className="px-6 py-4">
              <FieldGrid>
                <Field label="New password" htmlFor="account-new-password"><Input id="account-new-password" type="password" autoComplete="new-password" value={password.new_password} onChange={(e) => setPassword({ ...password, new_password: e.target.value })} /></Field>
                <Field label="Confirm new password" htmlFor="account-confirm-password"><Input id="account-confirm-password" type="password" autoComplete="new-password" value={password.confirm_password} onChange={(e) => setPassword({ ...password, confirm_password: e.target.value })} /></Field>
              </FieldGrid>
              <div className="mt-3 flex justify-end"><Action onClick={changePassword} disabled={busy} reason="Working">Save password</Action></div>
            </div>
          )}
        </Divided>
      </Panel>

      <SaveBar onSave={save} onRevert={() => setProfile(saved)} saveLabel="Save account" dirty={dirty} busy={busy} />
    </div>
  );
};

/* ── Connectors ───────────────────────────────────────────────────────── */

export const AppsPermissionsPanel: React.FC<{ clientId: string }> = () => {
  const [initialTool] = useState<string | null>(() => takePendingPermissionsTool());
  return <ConnectorsPanel mode="owner" initialTool={initialTool} />;
};

/* ── AI performance ────────────────────────────────────────────────────── */

export const AiPerformancePanel: React.FC = () => {
  const navigate = useNavigate();
  const prefs = useAsync<MyPreferencesResponse>(() => aaiApi.getMyPreferences(), 'Could not load the AI policy.');
  const ai = useAsync<AiControl>(() => aaiApi.getAiControl(), 'Could not load the AI control plane.');
  if (prefs.loading || ai.loading) return <Loading label="Loading AI policy" />;
  if (prefs.error || !prefs.data || ai.error || !ai.data) return <ErrorNote message={prefs.error || ai.error || 'Something went wrong.'} />;
  const b = prefs.data.boundary;
  const data = ai.data;
  const managed = data.managed.status === 'active' ? 'Approved pool' : 'Direct provider access';

  return (
    <div className="mx-auto max-w-[1000px]">
      <Banner tone="quiet" size="lg" kicker="Current policy" title={b.ai.policy} action={<Chip tone="private">Protected</Chip>} />
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <Stat label="Protected" value="Workflows & sources" evidence="Tools · permissions · owners" />
        <Stat label="Routing" value={managed} evidence={`${data.managed.routes.length} approved routes, policy controlled`} />
        <Stat label="Release" value="Evidence required" evidence="Evaluation · rollout · fallback" />
      </div>
      <Panel className="mt-5">
        <FactList
          facts={[
            { label: 'Default policy', value: b.ai.policy, right: <Chip>Set by Agents Anywhere</Chip> },
            { label: 'Representative evaluation before use', value: 'Required', right: <Chip tone="ok">Enforced</Chip> },
            { label: 'Approved fallback kept', value: 'Last approved route stays available', right: <Chip tone="ok">Enforced</Chip> },
            { label: 'Cleared tools', value: b.ai.cleared_tools },
          ]}
        />
      </Panel>
      <Panel className="mt-5">
        <PanelHead kicker="Written AI policy" title={data.policy.present ? 'Recorded' : 'Not recorded yet'} desc="Written by your AI Champion. You read it here." />
        <div className="px-6 pb-6">
          {data.policy.present && <p className="m-0 whitespace-pre-line text-[13.5px] leading-[22px] text-ink">{data.policy.body}</p>}
          {!data.policy.present && <p className="m-0 text-[13px] text-ink-dim">The company's AI policy appears here once it is recorded.</p>}
          {data.policy.updated_at && <div className="mt-2 text-[12px] text-ink-soft">Updated {relativeTime(data.policy.updated_at)}</div>}
        </div>
      </Panel>
      <div className="mt-6 flex flex-wrap gap-3">
        <Action size="lg" onClick={() => { closeSettings(); navigate('/controls/ai'); }}>View AI capabilities</Action>
        <Action size="lg" variant="quiet" disabled reason="The AI policy is written by your AI Champion">Save AI policy</Action>
      </div>
    </div>
  );
};

/* ── Quick Win controls ────────────────────────────────────────────────── */

export const QuickWinControlsPanel: React.FC = () => {
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load company capabilities.');
  const [open, setOpen] = useState<AaiSkill | null>(null);
  if (skills.loading) return <Loading label="Loading capabilities" />;
  if (skills.error || !skills.data) return <ErrorNote message={skills.error || 'Something went wrong.'} />;
  const rows = skills.data.skills.filter((s) => s.published);
  return (
    <div className="mx-auto max-w-[1000px]">
      <h3 className="m-0 mb-4 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink">{rows.length} governed {rows.length === 1 ? 'capability' : 'capabilities'}</h3>
      <Panel>
        {rows.length === 0 && <EmptyState title="No published capabilities yet" reason="A capability appears here once it is published for the company or a team." />}
        <Divided>
          {rows.map((s) => (
            <ListRow
              key={s.id}
              mark={<RouteMark kind="quick_win" size="lg" />}
              kicker={s.use_case_title || 'Published capability'}
              title={s.name}
              desc={`Published${s.created_at ? ` ${relativeTime(s.created_at)}` : ''} · ${audienceOf(s)}`}
              right={<Action size="sm" variant="ghost" arrow onClick={() => setOpen(s)}>Open</Action>}
            />
          ))}
        </Divided>
      </Panel>
      <Panel className="mt-5">
        <FactList
          facts={[
            { label: 'Human review required by default', value: 'A person reviews every result before it counts', right: <Chip tone="ok">Enforced</Chip> },
            { label: 'Evaluation before wider release', value: 'Team, department and company graduation is consent-first', right: <Chip tone="ok">Enforced</Chip> },
          ]}
        />
      </Panel>
      <div className="mt-6 flex justify-end">
        <Action size="lg" disabled reason="Release policy is governed by the AI Champion; these rules are platform enforced">Save release policy</Action>
      </div>
      {open && <CapabilityDrawer skill={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

/* ── People & departments ──────────────────────────────────────────────── */

/*
 * People & departments: the whole surface, not a summary of it.
 *
 * This was a read-only roster and a read-only department list, and every row on
 * it linked out to /team, which mounted the real thing. So the settings section
 * called "People & departments" was the one place you could not manage people
 * or departments, and the page it sent you to was a second address for a
 * surface that already had one here.
 *
 * It is the same shared component either way. What changed is that it renders
 * where the person already is.
 *
 * Company Context is not a tab here. It is a screen of its own in the sidebar,
 * and offering it a third time inside a settings section about people would be
 * the same duplication one level down.
 */
export const PeopleDepartmentsPanel: React.FC = () => {
  const clientId = localStorage.getItem('client_id') || '';
  const clientName = localStorage.getItem('client_name') || 'Your workspace';
  // Departments degrade differently from the roster: the roster is read-only
  // without manage authority and says so, but the departments form would only
  // collect 403s. Starts narrow and widens when the answer lands, so a slow
  // reply never shows more than it should.
  const [canManage, setCanManage] = useState(false);
  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    agentsApi.getWorkspace(clientId)
      .then(({ data }) => { if (!cancelled) setCanManage(!!data?.can_manage); })
      .catch(() => { if (!cancelled) setCanManage(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const tabs: CompanyTabId[] = canManage ? ['team', 'departments'] : ['team'];

  return (
    <div className="mx-auto max-w-[1000px]">
      <CompanyTabs clientId={clientId} clientName={clientName} initialTab="team" tabs={tabs} />
    </div>
  );
};

/* ── Roles & access ────────────────────────────────────────────────────── */

/*
 * One source: the grants view. It carries every role with what it holds by
 * default, what this company overrides, and whether the caller may change it.
 * A role opens in place and its permissions read as one table with a switch
 * per row, so there is no matrix of eight columns and no second window.
 */

type GrantRole = GrantsView['roles'][number];

const permissionName = (name: string): string => name.replace(/^AAI:\s*/i, '');

const RoleView: React.FC<{
  role: GrantRole;
  data: GrantsView;
  onBack: () => void;
  onChanged: () => void;
}> = ({ role, data, onBack, onChanged }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const held = new Set(role.effective);
  const rows = [...data.permissions].sort((a, b) => {
    const ha = held.has(a.id);
    const hb = held.has(b.id);
    if (ha !== hb) return ha ? -1 : 1;
    return permissionName(a.name).localeCompare(permissionName(b.name));
  });
  const label = roleLabel(role.value) === role.value ? role.label : roleLabel(role.value);
  const canEdit = (id: string): boolean => data.can_manage && role.manageable && (data.actor_grants === null || data.actor_grants.includes(id));
  const overridden = Object.keys(role.overrides).length;

  const flip = async (permission: string, next: boolean) => {
    setBusy(permission);
    try {
      const isDefault = role.default.includes(permission);
      let allowed: boolean | null = next;
      if (next === isDefault) allowed = null;
      await aaiApi.setGrant(role.value, permission, allowed);
      onChanged();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'Could not change that grant.');
    } finally {
      setBusy(null);
    }
  };

  let note = 'Read only. Grants are managed by the platform, or by someone here who holds "manage grants below me".';
  if (data.can_manage && role.manageable) note = 'Switch a grant on or off for this role at your company. You can only hand on grants you hold yourself. Platform defaults stay as they are; a change here is an override for this company.';
  if (data.can_manage && !role.manageable) note = 'This role sits at or above yours, so its grants are read only for you.';

  return (
    <div className="mx-auto max-w-[1000px]">
      <Action size="sm" variant="ghost" icon={<ArrowLeft size={14} />} onClick={onBack}>Roles &amp; access</Action>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">{label}</h2>
          <p className="m-0 mt-1 text-[13.5px] leading-[21px] text-ink-muted">{note}</p>
        </div>
        <span className="flex items-center gap-2">
          <Chip>{held.size} of {data.permissions.length} grants</Chip>
          {overridden > 0 && <Chip tone="warn">{overridden} overridden here</Chip>}
        </span>
      </div>
      <table className="mt-5 w-full border-collapse">
        <thead>
          <tr className="border-b border-line">
            <th className="px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted">Grant</th>
            <th className="hidden px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted md:table-cell">What it allows</th>
            <th className="whitespace-nowrap px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted">{label}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const on = held.has(p.id);
            const isOverride = p.id in role.overrides;
            const editable = canEdit(p.id);
            return (
              <tr key={p.id} className="border-b border-line-soft">
                <td className="px-3 py-3 align-top text-[13.5px] font-medium text-ink">
                  <span className="flex flex-wrap items-center gap-2">
                    {permissionName(p.name)}
                    {isOverride && <Chip tone="warn">Overridden here</Chip>}
                  </span>
                </td>
                <td className="hidden px-3 py-3 align-top text-[13px] leading-[20px] text-ink-dim md:table-cell">{p.description || ''}</td>
                <td className="w-[1%] whitespace-nowrap px-3 py-3 align-top">
                  {editable && <Toggle checked={on} disabled={busy === p.id} label={`${permissionName(p.name)} for ${label}`} onChange={(next) => flip(p.id, next)} />}
                  {!editable && on && <Chip tone="ok">Yes</Chip>}
                  {!editable && !on && <span className="text-[12.5px] text-ink-soft">No</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const RolesAccessPanel: React.FC<{ clientId: string }> = () => {
  const grants = useAsync<GrantsView>(() => aaiApi.getGrants(), 'Could not load roles.');
  const [openRole, setOpenRole] = useState<string | null>(null);
  if (grants.loading) return <Loading label="Loading roles" />;
  if (grants.error || !grants.data) return <ErrorNote message={grants.error || 'Something went wrong.'} />;
  const data = grants.data;
  const roles = [...data.roles].sort((a, b) => a.rank - b.rank);
  const open = roles.find((r) => r.value === openRole) || null;
  if (open) return <RoleView role={open} data={data} onBack={() => setOpenRole(null)} onChanged={grants.reload} />;
  return (
    <div className="mx-auto max-w-[1000px]">
      <span className="text-[13px] text-ink-muted">{roles.length} roles · {data.permissions.length} grants · defined by Agents Anywhere, assigned under People &amp; departments</span>
      <table className="mt-4 w-full border-collapse">
        <thead>
          <tr className="border-b border-line">
            <th className="px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted">Role</th>
            <th className="hidden px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted sm:table-cell">Grants</th>
            <th className="hidden px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted md:table-cell">At this company</th>
            <th className="px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted"><span className="sr-only">Open</span></th>
          </tr>
        </thead>
        <tbody>
          {roles.map((r) => {
            const label = roleLabel(r.value) === r.value ? r.label : roleLabel(r.value);
            const overridden = Object.keys(r.overrides).length;
            let here: React.ReactNode = <span className="text-ink-dim">Platform defaults</span>;
            if (overridden > 0) here = <Chip tone="warn">{overridden} overridden</Chip>;
            return (
              <tr
                key={r.value}
                role="button"
                tabIndex={0}
                onClick={() => setOpenRole(r.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenRole(r.value); } }}
                className="cursor-pointer border-b border-line-soft transition-colors hover:bg-canvas-inset focus-visible:bg-canvas-inset focus-visible:outline-none"
              >
                <td className="px-3 py-3 text-[13.5px] text-ink">
                  <span className="flex items-center gap-3">
                    <RouteMark glyph={label.charAt(0).toUpperCase()} />
                    <span className="font-medium">{label}</span>
                  </span>
                </td>
                <td className="hidden px-3 py-3 text-[13.5px] text-ink-muted sm:table-cell">{r.effective.length} of {data.permissions.length}</td>
                <td className="hidden px-3 py-3 text-[13.5px] md:table-cell">{here}</td>
                <td className="w-[1%] whitespace-nowrap px-3 py-3"><Action size="sm" variant="quiet" arrow onClick={() => setOpenRole(r.value)}>Open</Action></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Parked with company-level permissions: who may switch tools on for the company.
      <CompanyToolsCard className="mt-6" />
      */}
    </div>
  );
};

/* ── Organisation ──────────────────────────────────────────────────────── */

export const OrganisationPanel: React.FC<{ clientName: string }> = ({ clientName }) => {
  const policy = useAsync<AaiPolicy>(() => aaiApi.getPolicy(), 'Could not load the organisation policy.');
  const prefs = useAsync<MyPreferencesResponse>(() => aaiApi.getMyPreferences(), 'Could not load the boundary.');
  const b = prefs.data?.boundary;
  const notRecorded = (v: string | null | undefined): React.ReactNode => v || <NotMeasured reason="Not recorded" />;
  return (
    <div className="mx-auto max-w-[1000px]">
      <h3 className="m-0 mb-4 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink">{clientName}</h3>
      <Panel>
        <FactList
          facts={[
            { label: 'Privacy threshold', value: '4 people', right: <Chip>Platform rule</Chip> },
            { label: 'Operational history', value: notRecorded(b?.security.run_retention) },
            { label: 'Data region', value: notRecorded(b?.security.data_region) },
            { label: 'External sharing', value: notRecorded(b?.security.external_sharing) },
            { label: 'Human review for consequential actions', value: 'Money, contracts, customer output, access and release', right: <Chip tone="ok">Enforced</Chip> },
            { label: 'Aggregate-query protection', value: 'Groups under the threshold are suppressed', right: <Chip tone="ok">Enforced</Chip> },
            { label: 'Company knowledge', value: 'Facts stay Record only until a context owner approves them', right: <Chip tone="ok">Enforced</Chip> },
          ]}
        />
      </Panel>
      <Panel className="mt-5">
        <PanelHead kicker="Organisation policy" title={policy.data?.body ? 'Recorded' : 'Not recorded yet'} desc={policy.data?.updated_at ? `Updated ${relativeTime(policy.data.updated_at)}${policy.data.updated_by ? ` by ${policy.data.updated_by}` : ''}` : 'Written by your AI Champion.'} />
        <div className="px-6 pb-6">
          {policy.loading && <Loading label="Loading policy" />}
          {policy.data?.body && <p className="m-0 whitespace-pre-line text-[13.5px] leading-[22px] text-ink">{policy.data.body}</p>}
          {policy.data && !policy.data.body && <p className="m-0 text-[13px] text-ink-dim">The written policy appears here once it is recorded.</p>}
        </div>
      </Panel>
      <div className="mt-6 flex items-center justify-between gap-4 border-t border-line pt-5">
        <span className="text-[13px] text-ink-dim">Saving creates a new policy version.</span>
        <Action disabled reason="The organisation policy is written by your AI Champion">Save organisation policy</Action>
      </div>
    </div>
  );
};

/* ── Plan & usage ──────────────────────────────────────────────────────── */

/* The plan itself, its renewal and its meters are all drawn by PlanAndUsage,
 * which reads them from the one endpoint. This panel adds only the action the
 * Owner holds and nobody else does: changing the plan.
 *
 * The panel is shown to anyone with the department rollup grant, which is more
 * people than may change the plan, so the action asks for the billing grant
 * rather than riding on the panel. An offered button that bounces whoever
 * presses it is worse than no button. */
export const PlanPanel: React.FC<{ clientId: string }> = ({ clientId }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can('manage_client_billing');
  return (
    <div className="mx-auto max-w-[1000px] space-y-5">
      <PlanAndUsage clientId={clientId} />
      <div className="flex items-center justify-between gap-4 border-t border-line pt-5">
        <span className="text-[13px] text-ink-dim">
          {canManage && 'Changing the plan changes what the meters above are measured against.'}
          {!canManage && 'The plan is the Owner\'s to change. These meters are measured against it.'}
        </span>
        {canManage && (
          <Action variant="quiet" size="lg" onClick={() => navigate('/client/billing?tab=plans')}>Manage plan</Action>
        )}
      </div>
    </div>
  );
};

/* ── Activity & audit ──────────────────────────────────────────────────── */

const csvCell = (value: unknown): string => `"${String(value ?? '').replace(/"/g, '""')}"`;

export const ActivityAuditPanel: React.FC<{ clientId: string; clientName: string }> = ({ clientId, clientName }) => {
  const feed = useAsync<ActivityFeed>(() => aaiApi.getActivity('company', 100), 'Could not load activity.', [clientId]);
  const [selected, setSelected] = useState<ActivityEvent | null>(null);
  const events = feed.data?.events || [];
  const count = events.length;

  const exportCsv = () => {
    const header = ['time', 'event', 'category', 'actor', 'role', 'department', 'record', 'trust'];
    const lines = events.map((e) => [
      e.created_at, e.label, e.category, e.actor_email, e.actor_role, e.department, e.entity_title, e.trust,
    ].map(csvCell).join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(clientName || 'company').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-activity.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-[1000px]">
      <h3 className="m-0 text-[26px] font-bold leading-[32px] tracking-[-0.015em] text-ink">
        {count === 1 ? '1 visible event' : `${count} visible events`}
      </h3>
      <p className="m-0 mt-2 mb-5 text-[14px] leading-[23px] text-ink-muted">
        Visibility follows the linked person, department, Project, client assignment, or control entitlement. The activity label does not grant access.
      </p>
      {feed.loading && <Loading label="Loading activity" />}
      {feed.error && <ErrorNote message={feed.error} />}
      {!feed.loading && !feed.error && count === 0 && (
        <EmptyState title="Nothing recorded yet" reason="Decisions, control requests and access changes appear here as they happen." />
      )}
      {count > 0 && (
        <div className="divide-y divide-line overflow-hidden rounded-card border border-line bg-white">
          {events.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setSelected(e)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-canvas-inset"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-card bg-canvas-inset text-[13px] font-bold text-ink">
                {(e.actor_email || 'A').charAt(0).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink">
                {e.label}
                {e.entity_title && <span className="font-normal text-ink-dim"> · {e.entity_title}</span>}
              </span>
              <span className="shrink-0 text-[12.5px] text-ink-dim">{relativeTime(e.created_at)}</span>
              <span aria-hidden className="shrink-0 text-ink-soft">→</span>
            </button>
          ))}
        </div>
      )}
      <Action className="mt-5" variant="quiet" size="lg" onClick={exportCsv} disabled={count === 0} reason="Nothing to export yet">
        Export visible activity
      </Action>
      {selected && (
        <EventDrawer event={selected} clientName={clientName} kicker="Activity" layer="modal" links={ownerLinks} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};
